import { useCallback, useMemo, useState } from 'react'
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { toast } from 'sonner'

import {
  EntityDisplayTab,
  EntityRegistrationSection,
  EntityActionsTab,
  EntityAttributeDialog,
  SortableEntityAttributeRow,
} from '@/admin/pages/manage/entities/entity-editor-page'
import {
  useEntityTypes,
  useCreateEntityAttribute,
  useDeleteEntityAttribute,
  useReorderEntityAttributes,
  useUpdateEntityType,
  type EntityAttributeDefinition,
  type EntityFieldType,
  type EntityTypeDefinition,
} from '@/admin/hooks/use-entities'
import { toastApiError } from '@/shared/lib/api-error'
import {
  DEFAULT_ICON_KEY,
} from '@/admin/components/category-icons'
import { LucideIconPicker } from '@/shared/components/ui/lucide-icon-picker'
import { AttributeDeleteDialog } from '@/admin/components/shared/attribute-delete-dialog'
import {
  AttributeTemplatePicker,
  type AttributeTemplatePickResult,
  type AttributeTemplateResult,
} from '@/admin/components/shared/attribute-template-picker'
import { useResourceLookupProviders } from '@/admin/hooks/use-resource-lookup-providers'
import { ResourceLookupContextProvider } from '@/shared/components/ui/resource-lookup-context'
import { useHashTab, MANAGE_TEMPLATE_TABS } from '@/admin/hooks/use-hash-tab'
import { Button } from '@/shared/components/ui/button'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/shared/components/ui/tabs'
import { useTranslation } from 'react-i18next'

const UNDELETABLE_CORE_COLUMNS = new Set(['email', 'first_name', 'last_name'])

export function CustomerSettingsPage() {
  const {
    data: entityTypes = [],
    isLoading,
    isError,
    refetch,
  } = useEntityTypes()

  const consumerEntity = entityTypes.find((entity) => entity.key === 'consumer')

  return (
    <div className="mt-6">
      <ConsumerEntityGate
        isLoading={isLoading}
        isError={isError}
        onRetry={refetch}
        entity={consumerEntity}
      >
        {(entity) => <CustomerDetail entity={entity} allEntities={entityTypes} />}
      </ConsumerEntityGate>
    </div>
  )
}

function CustomerDetail({
  entity,
  allEntities,
}: {
  entity: EntityTypeDefinition
  allEntities: EntityTypeDefinition[]
}) {
  const [tab, setTab] = useHashTab(MANAGE_TEMPLATE_TABS, "general")
  return (
    <Tabs value={tab} onValueChange={setTab} className="gap-4">
      <TabsList variant="line">
        <TabsTrigger value="general">General</TabsTrigger>
        <TabsTrigger value="registration">Registration</TabsTrigger>
        <TabsTrigger value="display">Display</TabsTrigger>
        <TabsTrigger value="actions">Actions</TabsTrigger>
      </TabsList>

      <TabsContent value="general">
        <GeneralTab entity={entity} allEntities={allEntities} />
      </TabsContent>

      <TabsContent value="registration">
        <EntityRegistrationSection
          key={entity.uuid}
          entity={entity}
          allEntities={allEntities}
        />
      </TabsContent>

      <TabsContent value="display">
        <EntityDisplayTab
          entity={entity}
          allEntities={allEntities}
          sections={['registration_card']}
        />
      </TabsContent>

      <TabsContent value="actions">
        <EntityActionsTab entityUuid={entity.uuid} entityKey={entity.key} />
      </TabsContent>
    </Tabs>
  )
}

function GeneralTab({
  entity,
  allEntities,
}: {
  entity: EntityTypeDefinition
  allEntities: EntityTypeDefinition[]
}) {
  const [editing, setEditing] = useState<EntityAttributeDefinition | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [prefillName, setPrefillName] = useState('')
  const [deletingAttribute, setDeletingAttribute] =
    useState<EntityAttributeDefinition | null>(null)

  const remove = useDeleteEntityAttribute(entity.uuid)
  const reorder = useReorderEntityAttributes(entity.uuid)
  const updateEntity = useUpdateEntityType()
  const addAttr = useCreateEntityAttribute(entity.uuid)
  const resourceLookupProviders = useResourceLookupProviders()

  const attributes = useMemo(
    () =>
      [...(entity.attributes ?? [])].sort(
        (a, b) => a.order - b.order || a.display_name.localeCompare(b.display_name),
      ),
    [entity.attributes],
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return

      const oldIndex = attributes.findIndex((attr) => attr.uuid === active.id)
      const newIndex = attributes.findIndex((attr) => attr.uuid === over.id)
      if (oldIndex === -1 || newIndex === -1) return

      const ordered = arrayMove(attributes, oldIndex, newIndex).map((attr, index) => ({
        uuid: attr.uuid,
        order: index,
      }))
      reorder.mutate(ordered, {
        onError: (err) => toastApiError(err, 'Failed to reorder attributes'),
      })
    },
    [attributes, reorder],
  )

  async function handleAddAttrPick(result: AttributeTemplatePickResult) {
    if (result.type === 'create') {
      setPrefillName(result.name ?? '')
      setShowCreate(true)
      return
    }
    if (result.type === 'existing') {
      const tpl: AttributeTemplateResult = result.template
      try {
        await addAttr.mutateAsync({
          display_name: tpl.display_name,
          code_name: tpl.code_name,
          description: tpl.description ?? '',
          field_type: tpl.field_type as EntityFieldType,
          source: 'EAV',
          required: false,
          editable: true,
          configuration_data: tpl.configuration_data ?? {},
          order: attributes.length,
        })
        toast.success('Attribute added')
      } catch (err) {
        toastApiError(err, 'Failed to add attribute')
      }
    }
  }

  return (
    <ResourceLookupContextProvider providers={resourceLookupProviders}>
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-3">
        <div>
          <h3 className="text-[12px] font-medium uppercase tracking-[0.12em] text-[var(--ink-2)]">
            Icon
          </h3>
          <p className="text-[10px] text-[var(--ink-3)]">
            Choose an icon to represent consumers throughout the UI.
          </p>
        </div>
        <LucideIconPicker
          value={entity.icon || DEFAULT_ICON_KEY}
          onChange={(key) => {
            updateEntity.mutate(
              { uuid: entity.uuid, icon: key || '' },
              { onError: (err) => toastApiError(err, 'Failed to update icon') },
            )
          }}
          mode="floating"
        />
      </section>

      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-[12px] font-medium uppercase tracking-[0.12em] text-[var(--ink-2)]">
              Attributes
            </h3>
            <p className="text-[10px] text-[var(--ink-3)]">
              Core consumer columns and EAV fields that make up the consumer
              schema.
            </p>
          </div>
          <AttributeTemplatePicker
            onPick={handleAddAttrPick}
            resetAfterPick
            asAddButton
            domain="entity"
            addButtonLabel="Add attribute"
            data-testid="customers-add-attribute-picker"
          />
        </div>

        <div className="overflow-hidden rounded-[6px] border-[0.5px] border-[var(--rule)]">
          {attributes.length === 0 ? (
            <div className="bg-[var(--bg)] px-3 py-4 text-center text-[11px] text-[var(--ink-3)]">
              No attributes yet.
            </div>
          ) : (
            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext
                items={attributes.map((attribute) => attribute.uuid)}
                strategy={verticalListSortingStrategy}
              >
                {attributes.map((attribute) => {
                  const undeletable =
                    attribute.source === 'COLUMN' &&
                    UNDELETABLE_CORE_COLUMNS.has(attribute.code_name)
                  return (
                    <SortableEntityAttributeRow
                      key={attribute.uuid}
                      attribute={attribute}
                      isFirst={attribute.uuid === attributes[0]?.uuid}
                      onEdit={() => setEditing(attribute)}
                      onDelete={() => setDeletingAttribute(attribute)}
                      hideDelete={undeletable}
                    />
                  )
                })}
              </SortableContext>
            </DndContext>
          )}
        </div>
      </section>

      {showCreate ? (
        <EntityAttributeDialog
          entity={entity}
          allEntities={allEntities}
          open={showCreate}
          onOpenChange={(next) => {
            setShowCreate(next)
            if (!next) setPrefillName('')
          }}
          prefillName={prefillName}
        />
      ) : null}

      {editing ? (
        <EntityAttributeDialog
          entity={entity}
          allEntities={allEntities}
          attribute={editing}
          open={!!editing}
          onOpenChange={(next) => {
            if (!next) setEditing(null)
          }}
        />
      ) : null}

      <AttributeDeleteDialog
        open={!!deletingAttribute}
        onOpenChange={(next) => {
          if (!next) setDeletingAttribute(null)
        }}
        attributeName={deletingAttribute?.display_name}
        description="This removes the attribute from the consumer schema. Existing operational displays and mappings that reference it may need to be reviewed."
        successMessage="Attribute deleted"
        isPending={remove.isPending}
        onDelete={({ force }) =>
          remove.mutateAsync({ attrUuid: deletingAttribute!.uuid, force })
        }
      />
    </div>
    </ResourceLookupContextProvider>
  )
}

function ConsumerEntityGate({
  isLoading,
  isError,
  onRetry,
  entity,
  children,
}: {
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  entity: EntityTypeDefinition | undefined
  children: (entity: EntityTypeDefinition) => React.ReactNode
}) {
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex h-24 animate-pulse items-center justify-center rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] text-[12px] text-[var(--ink-3)]"
      >
        Loading entity catalog…
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex flex-col gap-2 rounded-[8px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg-subtle)] p-4 text-[12px] text-[var(--ink-2)]">
        <span>Couldn't load the entity catalog.</span>
        <Button variant="outline" size="sm" onClick={onRetry} className="self-start">
          {t('common.retry', { defaultValue: 'Retry' })}
        </Button>
      </div>
    )
  }

  if (!entity) {
    return (
      <div className="rounded-[8px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg-subtle)] p-4 text-[12px] text-[var(--ink-2)]">
        <p className="font-medium">Platform bootstrap error</p>
        <p className="mt-1 text-[var(--ink-3)]">
          The consumer entity is missing for this tenant. New tenants materialise
          this entity automatically; if you're seeing this message, the
          default-org bootstrap did not run for the tenant. Contact support.
        </p>
      </div>
    )
  }

  return <>{children(entity)}</>
}
